import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ShareMenu from '../components/ShareMenu';
import { antiqueItems as _seedA, slugify } from '../mockData';
import { useAntiques } from '../lib/usePublicData';
import { useT, localize } from '../lib/i18n';

const Annex = () => {
  const { t, lang } = useT();
  const [selectedKind, setSelectedKind] = useState('antique');
  const [selectedOrigin, setSelectedOrigin] = useState('All');
  const [lightbox, setLightbox] = useState(null);
  const { items: antiqueItems } = useAntiques();

  const origins = ['All', 'France', 'Ireland', 'Germany'];

  const filteredItems = antiqueItems.filter(item => {
    const kind = item.kind || 'antique';
    if (kind !== selectedKind) return false;
    return selectedOrigin === 'All' || item.origin === selectedOrigin;
  });

  return (
    <div className="min-h-screen bg-ivory">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-cream">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1717241364900-432b01d0bf89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxhcnQlMjBkZWNvJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgwNjUxNjIwfDA&ixlib=rb-4.1.0&q=85"
            alt="Antiques"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-cream/80 to-ivory/90"></div>
        </div>
        
        <div className="relative z-10 text-center px-6">
          <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
          <h1 className="text-7xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
            {t('annex.title')}
          </h1>
          <div className="h-1 w-40 bg-dark-crimson mx-auto mt-6 mb-6"></div>
          <p className="text-dark-brown text-2xl max-w-2xl mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('annex.subtitle')}
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 px-6 bg-cream border-y-2 border-gold/30">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Kind tabs: Antiques | Acquisitions */}
          <div className="flex justify-center gap-0 border-b-2 border-gold/40 pb-1">
            {['antique', 'acquisition'].map((kind) => (
              <button
                key={kind}
                onClick={() => setSelectedKind(kind)}
                data-testid={`tab-${kind}`}
                className={`px-12 py-4 text-lg tracking-wider transition-all border-b-4 -mb-1 ${
                  selectedKind === kind
                    ? 'border-dark-crimson text-dark-green'
                    : 'border-transparent text-dark-brown/60 hover:text-dark-green hover:border-gold/40'
                }`}
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {kind === 'antique' ? t('annex.tabAntique') : t('annex.tabAcquisition')}
              </button>
            ))}
          </div>

          {/* Origin filters */}
          <div className="flex flex-wrap justify-center gap-4">
            {origins.map((origin) => (
              <Button
                key={origin}
                onClick={() => setSelectedOrigin(origin)}
                className={`px-10 py-6 rounded-none border-2 transition-all duration-300 font-semibold tracking-wider ${
                  selectedOrigin === origin
                    ? 'bg-dark-crimson border-gold text-cream'
                    : 'bg-transparent border-rich-green text-dark-green hover:border-gold hover:bg-gold/10'
                }`}
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {origin === 'All' ? 'All Origins' : origin}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Antiques Grid */}
      <section className="py-20 px-6 bg-ivory deco-pattern">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <Card key={item.id} className="bg-cream border-2 border-gold/40 overflow-hidden group hover:border-dark-crimson transition-all duration-500 hover:shadow-2xl flex flex-col">
                <div
                  className="relative aspect-[4/5] overflow-hidden bg-cream cursor-zoom-in"
                  onClick={() => setLightbox({ image: item.image, title: item.title, size: item.period })}
                  data-testid={`antique-image-${item.id}`}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className={`absolute inset-0 w-full h-full object-contain p-3 transition-opacity duration-700 ${item.imageContext ? 'group-hover:opacity-0' : ''}`}
                  />
                  {item.imageContext && (
                    <img
                      src={item.imageContext}
                      alt={`${item.title} in setting`}
                      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                    />
                  )}
                  <Badge className="absolute top-4 right-4 bg-gold text-dark-brown hover:bg-dark-gold font-bold tracking-wider border-2 border-dark-crimson pointer-events-none">
                    {item.period}
                  </Badge>
                </div>
                <CardContent className="p-8 flex flex-col flex-1 bg-cream">
                  <Link
                    to={`/piece/annex/${slugify(`${item.title} ${item.period || ''}`)}`}
                    data-testid={`annex-detail-${item.id}`}
                  >
                    <h3 className="text-2xl font-bold text-dark-green mb-3 line-clamp-2 min-h-[3.5rem] hover:text-dark-crimson transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {localize(item, 'title', lang)}
                    </h3>
                  </Link>
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="text-dark-crimson shrink-0" size={16} />
                    <span className="text-dark-crimson font-semibold text-sm line-clamp-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.origin}</span>
                  </div>
                  <p className="text-charcoal mb-6 leading-relaxed line-clamp-4 flex-1" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem' }}>{localize(item, 'description', lang)}</p>
                  <div className="mt-auto pt-4 border-t-2 border-gold/40">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-gold">{item.price}</span>
                      <ShareMenu
                        variant="icon"
                        url={`${window.location.origin}/api/share/annex/${slugify(`${item.title} ${item.period || ''}`)}`}
                        title={`${item.title}${item.period ? ' — ' + item.period : ''}`}
                      />
                    </div>
                    <Link
                      to={`/contact?subject=${encodeURIComponent(`${t('common.inquireSubject')} ${item.title}`)}`}
                      className="block"
                      data-testid={`annex-inquire-${item.id}`}
                    >
                      <Button className="w-full bg-dark-crimson hover:bg-crimson text-cream py-5 rounded-none transition-all duration-300 hover:scale-105 border-2 border-gold font-semibold tracking-wider text-sm">
                        {t('common.requestInformation')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-24 px-6 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-8 border-2 border-dark-crimson"></div>
          <h2 className="text-5xl font-bold text-dark-green mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Authenticity Guaranteed
          </h2>
          <div className="h-1 w-32 bg-dark-crimson mx-auto mb-10"></div>
          <p className="text-charcoal text-xl leading-relaxed mb-10" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Every piece in our Antiques collection has been carefully authenticated and sourced from reputable 
            collections across France and Ireland. We provide full provenance documentation and certificates 
            of authenticity with each purchase.
          </p>
          <Button className="bg-transparent border-2 border-rich-green text-dark-green hover:bg-rich-green hover:text-cream px-12 py-6 rounded-none transition-all duration-300 font-semibold tracking-wider">
            Learn More About Our Process
          </Button>
        </div>
      </section>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute top-6 right-6 text-cream hover:text-gold transition-colors p-2"
          >
            <X size={32} />
          </button>
          <div className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-4">
            <img
              src={lightbox.image}
              alt={lightbox.title}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-[80vh] object-contain shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)]"
            />
            <div className="text-center text-cream" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <div className="text-2xl tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
                {lightbox.title}
              </div>
              {lightbox.size && (
                <div className="text-gold/80 text-sm mt-1">{lightbox.size}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Annex;
